import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  useTheme
} from '@mui/material';
import {
  MoveToInbox as MoveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { NewHire } from '../../services/hireService';
import { 
  moveHireService, 
  MoveHireRequest, 
  MoveHireResponse
} from '../../services/moveHireService';
import { progressStageService, ProgressStage } from '../../services/progressStageService';

interface MoveHireDialogProps {
  open: boolean;
  onClose: () => void;
  hire: NewHire | null;
  onMoveSuccess: (updatedHire: NewHire) => void;
}

const MoveHireDialog: React.FC<MoveHireDialogProps> = ({
  open,
  onClose,
  hire,
  onMoveSuccess
}) => {
  const theme = useTheme();
  const [targetProgress, setTargetProgress] = useState<number>(0);
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [progressStages, setProgressStages] = useState<ProgressStage[]>([]);

  // Reset state when dialog opens/closes or hire changes
  useEffect(() => {
    if (open && hire) {
      setTargetProgress(hire.progress);
      setReason('');
      setError(null);
      setSuccess(null);
      
      // Load progress stages for current user's organization
      const loadProgressStages = async () => {
        try {
          const stages = await progressStageService.getCurrentUserProgressStagesWithDbOverride();
          setProgressStages(stages);
        } catch (error) {
          console.error('Error loading progress stages:', error);
        }
      };
      loadProgressStages();
    }
  }, [open, hire]);

  const handleMove = async () => {
    if (!hire) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const request: MoveHireRequest = {
        hireId: hire.id,
        targetProgress,
        reason: reason.trim() || undefined,
        movedBy: 'Current User' // This will be set by the service
      };

      const response: MoveHireResponse = await moveHireService.moveHire(request);

      if (response.success && response.updatedHire) {
        setSuccess(response.message);
        // Wait a moment to show success message, then close and update
        setTimeout(() => {
          if (response.updatedHire) {
            onMoveSuccess(response.updatedHire);
          }
          onClose();
        }, 1500);
      } else {
        setError(response.error || 'Failed to move hire');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getCurrentStageInfo = (): ProgressStage | null => {
    return progressStages.find(stage => stage.value === hire?.progress) || null;
  };

  const getTargetStageInfo = (): ProgressStage | null => {
    return progressStages.find(stage => stage.value === targetProgress) || null;
  };

  const isMoveValid = (): boolean => {
    if (!hire) return false;
    const validValues = progressStages.map(s => s.value);
    return targetProgress !== hire.progress && validValues.includes(targetProgress);
  };

  const getMoveDirection = (): 'forward' | 'backward' | 'same' => {
    if (!hire) return 'same';
    if (targetProgress > hire.progress) return 'forward';
    if (targetProgress < hire.progress) return 'backward';
    return 'same';
  };

  const moveDirection = getMoveDirection();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
          backgroundImage: 'none',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px rgba(102, 126, 234, 0.2)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(102, 126, 234, 0.3)'}`,
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        backgroundImage: 'none',
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(102, 126, 234, 0.3)'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
      }}>
        <MoveIcon color="primary" />
        <Typography variant="h6" component="span">
          Move Hire: {hire?.fullName}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ 
        pt: 3,
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
        color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Current Stage Display */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Current Stage:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={getCurrentStageInfo()?.label || 'Unknown'} 
              color="primary" 
              variant="outlined"
            />
            <Typography variant="body2" color="textSecondary">
              {getCurrentStageInfo()?.description}
            </Typography>
          </Box>
        </Box>

        {/* Target Stage Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Target Stage</InputLabel>
          <Select
            value={targetProgress}
            label="Target Stage"
            onChange={(e) => setTargetProgress(e.target.value as number)}
            disabled={loading}
          >
            {progressStages.map((stage) => (
              <MenuItem key={stage.value} value={stage.value}>
                <Box>
                  <Typography variant="body1">
                    {stage.label}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {stage.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Target Stage Preview */}
        {targetProgress !== hire?.progress && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            borderRadius: 2, 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(102, 126, 234, 0.08)',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
          }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Moving to:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={getTargetStageInfo()?.label || 'Unknown'} 
                color={moveDirection === 'forward' ? 'success' : 'warning'}
                variant="filled"
              />
              <Typography variant="body2" color="textSecondary">
                {getTargetStageInfo()?.description}
              </Typography>
            </Box>
            {moveDirection === 'backward' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                You are moving this hire backwards in the process. Please ensure this is intentional.
              </Alert>
            )}
          </Box>
        )}

        {/* Reason Field */}
        <TextField
          fullWidth
          label="Reason for Move (Optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          rows={3}
          placeholder="Explain why this hire is being moved to this stage..."
          disabled={loading}
          sx={{ mb: 2 }}
        />

        {/* Validation Message */}
        {!isMoveValid() && hire && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please select a different target stage to move this hire.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        pt: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        backgroundImage: 'none',
        borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(102, 126, 234, 0.3)'}`,
      }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CancelIcon />}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleMove}
          disabled={loading || !isMoveValid()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          sx={{ 
            minWidth: 120,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            }
          }}
        >
          {loading ? 'Moving...' : 'Move Hire'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveHireDialog; 